export type MetricCounts = {
  reach: number | null;
  views: number | null;
  likes: number | null;
  comments: number | null;
  shares: number | null;
  saves: number | null;
  clicks: number | null;
};

export type LegacyPostMetricRow = MetricCounts & {
  platform: string;
  metric_day: string;
};

export type TargetPostMetricRow = LegacyPostMetricRow & {
  target_id: string;
  connection_id: string;
};

export type PostMetricDestination = Pick<
  TargetPostMetricRow,
  'target_id' | 'connection_id' | 'platform'
>;

export type MetricAggregate = Record<keyof MetricCounts, number>;

export interface TargetAwareMetricsResult {
  metrics: MetricAggregate;
  byPlatform: Record<string, MetricAggregate>;
  byDestination: Record<string, MetricAggregate>;
  coverage: { reportingDestinations: number; totalDestinations: number };
}

const METRIC_KEYS = ['reach', 'views', 'likes', 'comments', 'shares', 'saves', 'clicks'] as const;

function emptyMetrics(): MetricAggregate {
  return { reach: 0, views: 0, likes: 0, comments: 0, shares: 0, saves: 0, clicks: 0 };
}

function latestBy<T>(rows: readonly T[], key: (row: T) => string): T[] {
  const latest = new Map<string, T>();
  for (const row of rows) {
    const current = latest.get(key(row));
    if (
      !current ||
      String((row as { metric_day: string }).metric_day) >
        String((current as { metric_day: string }).metric_day)
    ) {
      latest.set(key(row), row);
    }
  }
  return [...latest.values()];
}

function add(into: MetricAggregate, row: MetricCounts): MetricAggregate {
  for (const key of METRIC_KEYS) into[key] += row[key] ?? 0;
  return into;
}

/**
 * Uses destination-grain metrics whenever the platform has them, with the
 * legacy parent-platform series retained only for platforms without any target
 * metrics. This makes a gradual cutover non-duplicative and preserves imported
 * history that cannot be attributed safely to an exact destination.
 */
export function aggregateTargetAwareMetrics(input: {
  legacyRows: readonly LegacyPostMetricRow[];
  targetRows: readonly TargetPostMetricRow[];
  destinations?: readonly PostMetricDestination[];
}): TargetAwareMetricsResult {
  const latestTargets = latestBy(input.targetRows, (row) => row.target_id);
  const targetPlatforms = new Set(latestTargets.map((row) => row.platform));
  const latestLegacy = latestBy(
    input.legacyRows.filter((row) => !targetPlatforms.has(row.platform)),
    (row) => row.platform,
  );

  const metrics = emptyMetrics();
  const byPlatform: Record<string, MetricAggregate> = {};
  const byDestination: Record<string, MetricAggregate> = {};

  for (const row of latestTargets) {
    const destination = add(emptyMetrics(), row);
    byDestination[row.target_id] = destination;
    byPlatform[row.platform] = add(byPlatform[row.platform] ?? emptyMetrics(), row);
    add(metrics, row);
  }
  for (const row of latestLegacy) {
    byPlatform[row.platform] = add(emptyMetrics(), row);
    add(metrics, row);
  }

  return {
    metrics,
    byPlatform,
    byDestination,
    coverage: {
      reportingDestinations: latestTargets.length,
      totalDestinations: input.destinations?.length ?? latestTargets.length,
    },
  };
}
